/**
 * SmartstartDialog - A multi-step dialog for provisioning devices using SmartStart QR codes.
 * 
 * This component implements the SmartStart provisioning workflow:
 * 1. Scan QR Code: User scans the device's QR code to capture the DSK (Device Specific Key)
 * 2. Send Code to Gateway: The DSK is sent to the gateway to prepare for device inclusion
 * 3. Power on Device: User powers on the device which automatically joins the network
 * 4. Complete: Device is successfully provisioned and paired
 * 
 * The dialog handles various edge cases including:
 * - Overwriting existing DSK codes
 * - Handling DSK conflicts (when a code is already in use by another device)
 * - Providing user feedback throughout the process
 * - Managing the stepper UI to show current progress
 * 
 * SmartStart allows devices to be provisioned without physical interaction with the gateway
 * by pre-registering the device's DSK before powering it on.
 */
import { PropertyManifestEntry } from "@features/home-manifest/types";
import { ManifestDevice } from "@features/plan-manifest/types";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import CodeScanner from "./CodeScanner";
import {useAppSelector} from "@app/hooks";
import {RootState} from "@app/store";
import {
  useCreateSmartstartEntryMutation,
  useUpdatePropertyManifestEntryMutation,
} from '../../api';
import {useSnackbar} from "notistack";
import {SnackType} from "@lib/types";
import {getErrorMessage} from "@lib/utils";
import ExpandableSnack from "@features/Common/ExpandableSnack";
import AlertDialog from "@features/Common/AlertDialog";

type Props = {
  entry: PropertyManifestEntry;
  isOpen: boolean;
  onClose: () => void;
}

const SmartstartDialog = ({
  isOpen,
  onClose,
  entry,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const userTenant = useAppSelector((state: RootState) => state.userTenant);
  const selectedTenant = useAppSelector((state: RootState) => state.tenant);
  const property = useAppSelector((state: RootState) => state.property);

  const currentTenantId = useMemo(
    () => (selectedTenant.currentTenant || userTenant)?.tenant_id,
    [selectedTenant.currentTenant, userTenant]
  );

  // State management for the SmartStart workflow
  const [qrCodeString, setQrCodeString] = useState<string | null>(null); // The DSK extracted from the scanned QR code
  const [confirmedOverwrite, setConfirmedOverwrite] = useState<boolean>(false); // Whether user confirmed overwriting existing DSK
  const [codeSent, setCodeSent] = useState<boolean>(false); // Whether the DSK has been sent to the gateway
  const [showForceDialog, setShowForceDialog] = useState<boolean>(false); // Whether to show DSK conflict resolution dialog
  const [forceDialogMessage, setForceDialogMessage] = useState<string | null>(null); // Error message for DSK conflict
  const [pendingQrCode, setPendingQrCode] = useState<string | null>(null); // QR code pending conflict resolution

  const [updatePropertyManifestEntry] =
    useUpdatePropertyManifestEntryMutation();
  const [createSmartstartEntry] = useCreateSmartstartEntryMutation();

  /**
   * Updates the manifest entry with the scanned QR code (DSK) and handles DSK conflicts.
   * 
   * This function:
   * 1. Updates the manifest entry with the DSK from the QR code
   * 2. Handles DSK conflicts when the code is already in use by another device
   * 3. Shows appropriate error messages or success feedback
   * 4. Returns success/failure status for flow control
   * 
   * @param qrCodeString - The DSK extracted from the scanned QR code
   * @param force - Whether to force the update even if there's a DSK conflict
   * @returns Promise<boolean> - True if update was successful, false otherwise
   */
  const updateManifestEntry = useCallback(
    async (qrCodeString: string, force = false) => {
      // Update the manifest entry with the DSK from the QR code
      const result = await updatePropertyManifestEntry({
        userTenantId: currentTenantId,
        propertyId: property?.property_id,
        entryId: entry.manifest_entry_id,
        body: {
          dsk: qrCodeString,
        },
        force,
      });

      if ('error' in result && result.error) {
        const msg = getErrorMessage(result.error);
        // Check if this is a DSK conflict error (status 400 with specific message)
        if (
          'status' in result.error && result.error.status === 400 &&
          typeof msg === 'string' &&
          msg.startsWith("DSK '") &&
          msg.includes('is already in use by another manifest entry')
        ) {
          // Show force dialog to allow user to move the DSK to this device
          setForceDialogMessage(msg);
          setPendingQrCode(qrCodeString);
          setShowForceDialog(true);
          return false;
        }
        // Show generic error for other types of failures
        enqueueSnackbar("Couldn't update property manifest:", {
          key: 'manifest-error',
          content: (
            <ExpandableSnack
              id="manifest-error"
              message={"Couldn't update property manifest:"}
              variant={SnackType.error}
              detail={msg}
            />
          ),
        });
        return false;
      }

      // Show success message
      enqueueSnackbar('Property manifest updated successfully', {
        variant: 'success',
      });
      return true;
    },
    [updatePropertyManifestEntry, currentTenantId, property?.property_id, entry.manifest_entry_id, enqueueSnackbar]
  );

  /**
   * Performs the complete SmartStart entry process: updates manifest and sends code to gateway.
   * 
   * This function implements a two-step process:
   * 1. Updates the manifest entry with the DSK (calls updateManifestEntry)
   * 2. Sends the DSK to the gateway for SmartStart provisioning
   * 
   * The gateway will use this DSK to automatically provision the device when it powers on.
   * 
   * @param qrCodeString - The DSK extracted from the scanned QR code
   */
  const addSmartstartEntry = useCallback(
    async (qrCodeString: string) => {
      // Step 1: Update the manifest entry with the DSK
      const manifestUpdateSuccess = await updateManifestEntry(qrCodeString);
      if (!manifestUpdateSuccess) return;

      // Step 2: Send the DSK to the gateway for SmartStart provisioning
      const result = await createSmartstartEntry({
        userTenantId: currentTenantId,
        propertyId: property?.property_id,
        devices: [{
          qr_code_string: qrCodeString,
          device_name: entry.manifest_entry_id,
          property_area: entry.property_area
        }]
      });

      if ('error' in result && result.error) {
        // Show error if gateway communication failed
        enqueueSnackbar("Couldn't send code to gateway:", {
          key: 'smartstart-error',
          content: (
            <ExpandableSnack
              id="smartstart-error"
              message={"Couldn't send code to gateway:"}
              variant={SnackType.error}
              detail={getErrorMessage(result.error)}
            />
          ),
        });
        return;
      }

      // Show success message when DSK is sent to gateway
      enqueueSnackbar('QR code data sent to gateway', {
        variant: 'success',
      });
    },
    [updateManifestEntry, createSmartstartEntry, currentTenantId, property?.property_id, entry.manifest_entry_id, entry.property_area, enqueueSnackbar]
  );

  /** Saves the DSK to manifest without sending to gateway, then closes dialog */
  const handleManifestUpdate = useCallback(async () => {
    if (!qrCodeString) return;
    const result = await updateManifestEntry(qrCodeString);
    if (result) {
      onClose();
    }
  }, [qrCodeString, updateManifestEntry, onClose]);

  /** Clears the scanned QR code to allow re-scanning */
  const handleClear = useCallback(() => {
    setQrCodeString(null);
  }, []);

  /** Sends the DSK to gateway and marks code as sent */
  const handleAddEntry = useCallback(() => {
    if (qrCodeString) {
      addSmartstartEntry(qrCodeString).then(() => {
        setCodeSent(true);
      });
    }
  }, [addSmartstartEntry, qrCodeString]);

  /** Handles QR code scan events and stores the DSK */
  const handleCodeScanned = useCallback((code: unknown) => {
    if (typeof code === 'string') {
      setQrCodeString(code);
    }
  }, []);

  /**
   * Calculates the current step in the SmartStart workflow based on the current state.
   * 
   * The SmartStart workflow has 4 steps:
   * 0. Scan QR Code: No QR code scanned yet
   * 1. Send Code: QR code scanned, ready to send to gateway
   * 2. Power On: Code sent to gateway, waiting for device to power on
   * 3. Complete: Device has joined the network (gateway_device_id is set)
   * 
   * @returns number - The current step (0-3)
   */
  const activeStep = useMemo(() => {
    if (!qrCodeString) return 0; // No QR code scanned yet
    if (!codeSent) return 1; // QR code scanned, ready to send
    if (!entry.gateway_device_id) return 2; // Code sent, waiting for device
    return 3; // Device has joined the network
  }, [codeSent, entry.gateway_device_id, qrCodeString]);

  /** Returns the heading text for the current step in the SmartStart workflow */
  const stepHeading = useMemo(() => {
    switch (activeStep) {
      case 0:
      default:
        return 'Scan the QR code';
      case 1:
        return 'Confirm and send code';
      case 2:
        return 'Power on the device';
      case 3:
        return 'Success';
    }
  }, [activeStep]);

  /** Returns the description text for the current step in the SmartStart workflow */
  const stepDescription = useMemo(() => {
    switch (activeStep) {
      case 0:
      default:
        return ((entry.dsk && !confirmedOverwrite)
          ? 'A code has already been scanned for this device. Please confirm before overwriting.'
          : 'Scan the device\'s QR code to begin provisioning. The code is located either on the device itself or on its packaging.'
        );
      case 1:
        return 'The code has been scanned successfully. Press the button below to transmit its contents to the Gateway.';
      case 2:
        return 'The code has been sent to the gateway. Power on the device, and wait for it to automatically join the network.';
      case 3:
        return 'Pairing is now complete!';
    }
  }, [activeStep, confirmedOverwrite, entry.dsk]);

  /** Confirms overwriting an existing DSK for this device */
  const handleConfirmOverwrite = useCallback(() => {
    setConfirmedOverwrite(true);
  }, []);

  /**
   * Handles the confirmation to move a DSK from another device to this one.
   * 
   * This is called when the user confirms they want to move a DSK that's already
   * in use by another manifest entry. The force parameter will be set to true
   * to override the conflict.
   */
  const handleForceMove = useCallback(async () => {
    if (pendingQrCode) {
      setShowForceDialog(false);
      // Update manifest entry with force=true to override DSK conflict
      const result = await updateManifestEntry(pendingQrCode, true);
      setPendingQrCode(null);
      setForceDialogMessage(null);
      if (result) {
        onClose();
      }
    }
  }, [pendingQrCode, updateManifestEntry, onClose]);

  /**
   * Handles cancellation of the DSK conflict resolution.
   * 
   * Clears all pending state and resets the QR code to allow the user
   * to scan a different code or start over.
   */
  const handleForceCancel = useCallback(() => {
    setShowForceDialog(false);
    setPendingQrCode(null);
    setForceDialogMessage(null);
    setQrCodeString(null);
  }, []);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
      >
        <DialogTitle>Pair {(entry.device as ManifestDevice).friendly_name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'stretch', maxWidth: '80vw', maxHeight: '80vh'}}>
          <Stepper activeStep={activeStep} variant="outlined">
            <Step>
              <StepLabel>Scan Code</StepLabel>
            </Step>
            <Step>
              <StepLabel>Send Code to Gateway</StepLabel>
            </Step>
            <Step>
              <StepLabel>Power on Device</StepLabel>
            </Step>
            <Step>
              <StepLabel>Complete</StepLabel>
            </Step>
          </Stepper>
          <Typography variant="h6" textAlign="center" mt={4}>
            {stepHeading}
          </Typography>
          <Typography variant="body1" textAlign="center" m={1}>
            {stepDescription}
          </Typography>
          {
            activeStep < 2
            ? <>
              {
              qrCodeString
              ? <Typography variant="overline" textAlign="justify" m={2} sx={{ lineHeight: 1.15, wordBreak: 'break-all', whiteSpace: 'wrap', textWrap: 'wrap',overflow: 'hidden' }}>
                {qrCodeString}
              </Typography>
              : <>
                {(entry.dsk && !confirmedOverwrite)
                ? <Button sx={{ mr: 'auto', ml: 'auto' }} color="warning" variant="contained" onClick={handleConfirmOverwrite}>Yes, overwrite</Button>
                : <CodeScanner onScanned={handleCodeScanned} />
                }
              </>
            }
            </> : null
          }
        </DialogContent>
        <DialogActions>
          {activeStep < 2 && (
            <>
              <Button
                onClick={handleClear}
                size="small"
                aria-label="Clear scanned code"
                disabled={!qrCodeString}
              >
                Re-scan
              </Button>
              <Button
                color="secondary"
                variant="outlined"
                onClick={handleManifestUpdate}
                disabled={!qrCodeString}
              >
                Save to provision later
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={codeSent ? onClose : handleAddEntry}
                disabled={!qrCodeString}
              >
                {codeSent ? 'Close' : 'Send Code'}
              </Button>
            </>
          )}
          {
            activeStep === 3
            ? <Button color="primary" onClick={onClose} variant="contained">
              Close
            </Button>
            : <Button color="secondary" onClick={onClose} >
              Cancel
            </Button>
          }
        </DialogActions>
      </Dialog>
      {showForceDialog && forceDialogMessage && (
        <AlertDialog
          title="DSK Already In Use"
          content={forceDialogMessage + '\n\nWould you like to move the code to this device?'}
          confirmButtonLabel="Move Code"
          cancelButtonLabel="Cancel"
          onConfirm={handleForceMove}
          onCancel={handleForceCancel}
        />
      )}
    </>
  );
}

export default SmartstartDialog;
